import React, { useState, useMemo } from "react";
import cx from "classnames";
import {
  TRUNCATE_THRESHOLD,
  extractSummaryText,
  extractReferences,
  processCitations,
} from "../../helpers/aiOverviewHelpers.js";
import UrlToLinks from "./UrlToLinks.jsx";
import CitationSegment from "./CitationSegment.jsx";
import TextWithDistributedCitations from "./TextWithDistributedCitations.jsx";

const AIOverview = ({
  serverData,
  id,
  dictionary,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const summaryText = useMemo(() => extractSummaryText(serverData), [serverData]);
  const references = useMemo(() => extractReferences(serverData), [serverData]);
  
  if (!summaryText) {
    return null;
  }

  const [finalIndex, citations] = useMemo(
    () => processCitations(
      serverData?.summary?.summaryWithMetadata?.citationMetadata?.citations
    ),
    [serverData]
  );

  const shouldTruncate = summaryText.length > TRUNCATE_THRESHOLD;
  const maxVisible = shouldTruncate && !isExpanded 
    ? TRUNCATE_THRESHOLD 
    : summaryText.length;
  
  const displayText = useMemo(
    () => isExpanded || !shouldTruncate 
      ? summaryText 
      : summaryText.substring(0, TRUNCATE_THRESHOLD),
    [summaryText, isExpanded, shouldTruncate]
  );

  const renderRemainingText = () => {
    if (finalIndex >= summaryText.length) {
      return null;
    }
    
    const shouldShowRemaining = isExpanded || !shouldTruncate || finalIndex < TRUNCATE_THRESHOLD;
    if (!shouldShowRemaining) {
      return null;
    }
    
    const remainingText = summaryText.substring(
      finalIndex,
      isExpanded || !shouldTruncate ? summaryText.length : TRUNCATE_THRESHOLD
    );
    
    return (
      <span>
        <UrlToLinks text={remainingText} keyPrefix={`${id}-remaining`} />
      </span>
    );
  };

  return (
    <div
      className={cx("ai-overview")}
      id={`${id}-results-summary`}
    >
      <div className="ai-overview__container">
        <div className="ai-overview__header">
          <div className="ai-overview__header-left">
            <svg 
              className="ai-overview__star-icon" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <h3 className="ai-overview__title">
              {dictionary.AiSummaryTitle || "AI Overview"}
            </h3>
          </div>
        </div>
        
        <div className="ai-overview__content">
          {citations && citations.length > 0 ? (
            <div className="ai-overview__text">
              {citations.map((citation, index) => (
                <CitationSegment
                  key={`citation-${index}`}
                  citation={citation}
                  summaryText={summaryText}
                  maxVisible={maxVisible}
                  references={references}
                  id={id}
                  index={index}
                />
              ))}
              {renderRemainingText()}
            </div>
          ) : (
            <div className="ai-overview__text">
              {references && references.length > 0 ? (
                <TextWithDistributedCitations
                  text={displayText}
                  references={references}
                  id={id}
                  keyPrefix={`${id}-display`}
                />
              ) : (
                <UrlToLinks text={displayText} keyPrefix={`${id}-display`} />
              )}
            </div>
          )}
          
          {shouldTruncate && (
            <button 
              className="ai-overview__show-more"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
            >
              {isExpanded ? "Show less" : "Show more"}
              <svg 
                className={cx("ai-overview__chevron", {
                  "ai-overview__chevron--expanded": isExpanded
                })} 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
              </svg>
            </button>
          )}
        </div>
        
        <div className="ai-overview__warning">
          {dictionary.AiWarning || 
            "Content generated by AI is provided for informational purposes only and may not be accurate or complete. Do not rely solely on this information for decisions. Always verify details with official sources."}
        </div>
      </div>
    </div>
  );
};

export default AIOverview;
